/*
XOL-67 (RTA redesign v2.1, V1): the words of the RTA drawer (`RtaDetails`), in pure functions. No Vue, no store, no
axios: the component passes who is looking and what the organisations are called, and the specs
drive them with plain objects.

  statusPanel(rta, ctx)   the status panel: one el-alert, a sentence per stage x actor
  resetNotice(rta, ctx)   the Tower Co's "your saved decision was cleared" notice (state.rta.lastReset)
  cue(rta, ctx)           the header's cue line: Your turn / Waiting on / the terminal line
  stageSuffix(rta)        "Stage 2 of 5" / "Completed" / "Rejected", after the reference in the header
  HINTS                   the fixed hints under the footer buttons (Back to Searching, Reject RTA…)

The copy is the v2 UX spec (docs/plans/2026-10-27-xol-67-rta-v2.md, section 4.1 to 4.4) and the mock beside it, under the v2.1 names: the stages are
Searching and Client Review, there are five steps (Tower Co Decision is Step 4, Finalize Step 5), and a Searching RTA may have no option at all. Placeholders are the
arguments: `telco` is the client (for a Tower Co client, that Tower Co), `towerCo` the Tower Co, `siteName` the completed site's name.

ctx { role, telco, towerCo, siteName, tcClient, embedded, org, userName, options, fmtDate, fmtDateTime, today, createdBy }
  role         'client' | 'tcc' (Tower Co closer) | 'tce' (any other Tower Co user, view-only too)
  tcClient     the client is a Tower Co, so no RTS exists (decision 4)
  embedded     inside a site drawer: no View site link (decision 25)
  org          the viewer's organisation (the cue is org-level, rtaStage.isMyTurn)
  userName     the viewer's name, to tell "You declined" from another manager's decline in the reset notice
  options      the RTA's option rows ([{ id, optionNo }]), so the reset notice shows an option's CURRENT number: `lastReset.optionId`
               is looked up here, because numbers shift when an option is deleted (P-S20)
  fmtDate      a function, the date as the app shows it (defaults to the raw value)
  createdBy    the API's `createdByParty`: 'towerco' (the Tower Co entered the request on the client's behalf: "You requested a site for ..."), 'owner' ("{Telco} requested a site"),
               anything else (null, or a RTA the API cannot tell): "A site was requested on {date}." (the no-options panel)
  today        'YYYY-MM-DD' on the app's clock (appNowDateSql()): the Completed panel says "at the next nightly run"
               for a handover date of today or earlier (B8, decision 86)

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

import {
  optionSummary, stepState, waitingOn, yourTurnTask, siteDeleted, allOptionsDeclined, noOptions, hasBeenProposed, proposedToOf
} from '@/lib/rtaStage'
import { ymd as siteYmd } from '@/lib/siteTransition'

// The fixed hints under the footer buttons (UX spec 4.4). The Propose hint depends on who is e-mailed: rtaOption.proposeHint.
export var HINTS = {
  backToDraft: 'Takes the options back to Draft so they can be changed. Nobody is e-mailed.',
  reject: 'Ends this RTA as Rejected. You\'ll be asked for a reason.'
}

function fmt (fn, value) {
  if (value === undefined || value === null || value === '') {
    return ''
  }

  return typeof fn === 'function' ? String(fn(value)) : String(value)
}

// " on 26 Oct 2026", or nothing when the date is not known.
function on (text) {
  return text ? ' on ' + text : ''
}

// "option 2", or "an option" when the server sent no number.
function option (n) {
  return n ? 'option ' + n : 'an option'
}

function rtaState (rta) {
  var s = rta && rta.state

  return s && s.rta && typeof s.rta === 'object' ? s.rta : {}
}

/**
 * The status panel: { type (el-alert), title, description, links, deleted } or null for a status this
 * app does not know. `links` are the Completed panel's buttons on Step 5 ('site', 'rts'); `deleted`
 * means the site is gone and "Site deleted" takes their place (decision 57).
 */
export function statusPanel (rta, ctx) {
  if (!rta) {
    return null
  }

  var c = ctx || {}
  var role = c.role || 'client'
  var client = role === 'client'
  var tcc = role === 'tcc'
  var tc = c.towerCo || 'the Tower Co'
  var telco = c.telco || 'the client'
  var site = c.siteName || 'the site'
  var s = optionSummary(rta)
  var changed = s.approvedChangedByParty !== null
  var n = s.approvedOptionNo || s.finalOptionNo
  var k = n
  var done = { type: 'info', title: '', description: '', links: [], deleted: false }

  function panel (type, title, description) {
    return { type: type, title: title, description: description || '', links: [], deleted: false }
  }

  switch (rta.status) {
    case 'Searching':
      return draftPanel(rta, c, panel)

    case 'Client Review':
      return reviewPanel(rta, c, s, panel)

    case 'Client Approved':
      if (changed) {
        if (s.approvedChangedByParty === 'towerco' && !client) {
          return panel('info', 'The review of ' + option(k) + ' was changed and not submitted. Submit it in the option, then decide.')
        }

        return client
          ? panel('info', 'You changed your review of ' + option(k) + '. Submit it so ' + tc + ' can decide.')
          : panel('warning', telco + ' changed its review of ' + option(k) + ' and hasn\'t submitted it. You can submit the decision once it does.')
      }

      // WHO approved needs `approvedByParty`; without it the sentence is neutral ("Option 2 is approved"), never a guess.
      var by = s.approvedByParty
      var approved = by === 'owner' ? telco + ' approved ' + option(n) : (n ? 'Option ' + n : 'An option') + ' is approved'

      if (client) {
        return panel('info', (by === 'owner' ? 'You approved ' + option(n) : approved) + '. Waiting on ' + tc + '\'s decision.')
      }

      return tcc
        ? panel('success', approved + '. Decide in Step 4, then press Submit decision.')
        : panel('info', approved + '. A project, finance or admin manager decides next.')

    case 'Finalize':
      if (client) {
        return panel('info', tc + ' is setting up the site from ' + option(n) + '.')
      }

      if (tcc) {
        return panel('info', c.tcClient ? 'Fill in the new site in Step 5, then press Complete RTA.' : 'Fill in the new site and the RTS in Step 5, then press Complete RTA.')
      }

      return panel('info', (n ? 'Option ' + n + ' is approved.' : 'An option is approved.') + ' A project, finance or admin manager completes the RTA.')

    case 'Closed':
      return completedPanel(rta, c, role, site, telco, n)

    case 'Rejected':
      // The reason follows as ReadOnlyRemarks "Reason for rejecting" under the panel (the drawer's); `completed_by` is the name that rejected it.
      done.type = 'error'
      done.title = 'Rejected by ' + (rta.completed_by ? rta.completed_by + ' (' + tc + ')' : tc) + on(fmt(c.fmtDate, rta.completed_at)) + '.'

      return done
  }

  return null
}

// The viewer's side as the API names a party: 'owner' (the client) or 'towerco'.
function viewerParty (role) {
  return role === 'client' ? 'owner' : 'towerco'
}

// Searching (UX spec 4.2): own side searching (no option yet / first round / back in Searching / every option declined), or the other side.
function draftPanel (rta, c, panel) {
  var role = c.role || 'client'
  var tc = c.towerCo || 'the Tower Co'
  var telco = c.telco || 'the client'
  var drafter = rta.draftParty || rtaState(rta).draftParty || 'towerco'
  var own = c.tcClient === true || drafter === viewerParty(role)
  var drafterName = drafter === 'owner' ? telco : tc
  var proposeTo = c.tcClient === true ? '' : ' to ' + (role === 'client' ? tc : telco)

  if (allOptionsDeclined(rta)) {
    // P-S21: the ways forward are adding an option, deleting the declined ones (Option 1 too: decision 155) or Reject RTA…. A side that cannot
    // reject says who can. Only the Tower Co deletes a Declined option, so only the closer's line offers the delete.
    if (own) {
      if (role === 'tcc') {
        return panel('warning', 'Every option is declined. Add an option and propose again, delete the declined ones, or reject the RTA.')
      }

      return panel('warning', 'Every option is declined. Add an option and propose again, or ask ' +
        (role === 'client' ? tc : 'a project, finance or admin manager') + ' to reject the RTA.')
    }

    // Neutral on purpose: after a Step 4 Decline it was the Tower Co that declined, not the viewer.
    return panel('info', 'Every option is declined. Waiting on ' + drafterName + ' to add an option.')
  }

  // A normal Searching state (decisions 153, 155; P-S31): a new request has no option (after create `draftParty` is the Tower Co), and Option 1 can be
  // deleted. UX spec 4.2: the Tower Co is told who requested the site and what to do; the client is told its request is with the Tower Co. When the
  // CLIENT holds the search (it added options and deleted them) the ordinary Searching lines below say what to do.
  if (noOptions(rta) && drafter === 'towerco') {
    if (c.tcClient === true) {
      return panel('info', 'Add the options you find, then propose them.')
    }

    if (role === 'client') {
      return panel('info', 'Your request is with ' + tc + '. It adds the options it finds and proposes them to you for review.')
    }

    var when = on(fmt(c.fmtDate, rta.created_at))

    // Who requested it is the API's `createdByParty` ('towerco' | 'owner'; null on an RTA whose creator it cannot tell). Unknown is said neutrally, never guessed.
    var todo = '. Add the options you find, then propose them to ' + telco + '.'

    if (c.createdBy === 'towerco') {
      return panel('info', 'You requested a site for ' + telco + when + todo)
    }

    return panel('info', c.createdBy === 'owner'
      ? telco + ' requested a site' + when + todo
      : 'A site was requested for ' + telco + when + todo)
  }

  if (!own) {
    return panel('info', drafterName + ' is preparing the options. They come to you for review when ' + drafterName + ' proposes them.')
  }

  if (hasBeenProposed(rta)) {
    // `state.rta.draftSince` stamps every entry into Searching (create included, so it proves nothing about a proposal: hasBeenProposed ignores it); it
    // dates "Back in Searching since". Without it the sentence loses its date.
    var since = fmt(c.fmtDate, rtaState(rta).draftSince)

    return panel('info', 'Back in Searching' + (since ? ' since ' + since : '') + '. Change the options, then propose them again.')
  }

  return panel('info', 'Searching. Add or change options, then press Propose Option(s)' + proposeTo + '.')
}

// Client Review (UX spec 4.2): the options were proposed to the viewer's side, or to the other.
function reviewPanel (rta, c, s, panel) {
  var role = c.role || 'client'
  var tc = c.towerCo || 'the Tower Co'
  var telco = c.telco || 'the client'
  var to = proposedToOf(rta) || 'owner'
  var when = on(fmt(c.fmtDate, rtaState(rta).proposedAt))
  var count = s.inReviewCount

  if (c.tcClient === true) {
    return panel('warning', 'The options are in Client Review. Open each option and submit a review: approve one, or decline the ones that don\'t fit.')
  }

  if (to === viewerParty(role)) {
    var from = to === 'owner' ? tc : telco

    return panel('warning', from + ' proposed ' + (count > 0 ? count + ' ' + (count === 1 ? 'option' : 'options') : 'the options') + when +
      '. Open each option and submit a review: approve one, or decline the ones that don\'t fit.')
  }

  return panel('info', 'Proposed to ' + (to === 'owner' ? telco : tc) + when + '. Waiting for its review.')
}

// 'YYYY-MM-DD' of a date value, for a string comparison with `today` (never parsed: XOL-414).
function ymd (v) {
  return String(v || '').slice(0, 10)
}

// Is there a handover date? A stored zero date (0000-00-00) is none, as on the API (decision 206).
function hasDate (v) {
  return siteYmd(v) !== ''
}

// What the site is now, from API DEV-38's `completion.site { id, status, handover, handoverStatus }` (decisions 83, 85, 105):
// a Candidate site with a pending transition, a Candidate site with a status and no date yet, a Candidate site with none, any other status (the transition has run: nothing
// more is said of it), or, with no `completion.site`, nothing.
function siteNow (rta, c, site) {
  var cs = rta && rta.completion && typeof rta.completion === 'object' ? rta.completion.site : null

  if (!cs || typeof cs !== 'object' || !cs.status) {
    return ''
  }

  if (cs.status !== 'Candidate') {
    return ' ' + site + ' is ' + cs.status + '.'
  }

  if (!cs.handoverStatus) {
    // Decision 194: nothing scheduled is a state Complete allows now. A legacy row with a date and no status keeps the plain line.
    return hasDate(cs.handover) ? ' ' + site + ' is a Candidate site.' : ' ' + site + ' is a Candidate site with no handover date.'
  }

  if (!hasDate(cs.handover)) {
    return ' ' + site + ' is a Candidate site, becoming ' + cs.handoverStatus + ' once a handover date is set.'
  }

  var past = !!c.today && hasDate(cs.handover) && ymd(cs.handover) <= c.today

  return ' ' + site + ' is a Candidate site, becoming ' + cs.handoverStatus +
    (past ? ' at the next nightly run (5:00 am Malaysia time).' : on(fmt(c.fmtDate, cs.handover)) + '.')
}

// When a Completed RTA was completed: `completed_at`, else (DEV-49, a legacy row: M6 maps its `approved_at`, not `completed_at`)
// `approved_at`. A Rejected RTA has `completed_at` only.
function completedAt (rta) {
  return rta && (rta.completed_at || rta.approved_at) ? (rta.completed_at || rta.approved_at) : null
}

// The RTS Complete opened for a Telco client, or null (XOL-67 B10): a legacy RTA completed before RTAs opened one has none.
function rtsOf (rta) {
  var c = rta && rta.completion && typeof rta.completion === 'object' ? rta.completion : null

  return c && c.rtsId !== undefined && c.rtsId !== null && c.rtsId !== '' ? c.rtsId : null
}

// How Complete treated the RTS (XOL-693, decision 211): 'opened' | 'linked' | 'none', or null. A legacy row has a null outcome: with an `rtsId`
// it reads as 'opened', without one as nothing (the API's one read rule; the twin of rtaCompletion.rtsOutcomeOf, which this module does not import).
function rtsOutcome (rta) {
  var c = rta && rta.completion && typeof rta.completion === 'object' ? rta.completion : null

  if (c && (c.rtsOutcome === 'opened' || c.rtsOutcome === 'linked' || c.rtsOutcome === 'none')) {
    return c.rtsOutcome
  }

  return rtsOf(rta) !== null ? 'opened' : null
}

// The deleted-site panel's line about the RTS the site took with it: opened or linked at completion (F7); 'none' and no RTS add nothing.
function deletedRts (rta, c) {
  if (c.tcClient || !rtsOf(rta)) {
    return ''
  }

  return rtsOutcome(rta) === 'linked' ? ' The RTS linked at completion was deleted with the site.' : ' The RTS opened at completion was deleted with the site.'
}

// A Completed RTA: success with the links, or (decision 57) info with "site deleted" in their place.
function completedPanel (rta, c, role, site, telco, n) {
  // The site's LIVE name (API b8fc0d36: `completion.site.siteName`, which the client gets too) names the site on both sides; the caller's
  // `siteName` (the RTA's title) is the same value and stands in when the read has no `completion.site`.
  var cs = rta.completion && typeof rta.completion === 'object' ? rta.completion.site : null

  if (cs && typeof cs === 'object' && typeof cs.siteName === 'string' && cs.siteName.trim() !== '') {
    site = cs.siteName.trim()
  }

  var completed = on(fmt(c.fmtDate, completedAt(rta)))
  var gone = siteDeleted(rta)

  if (gone) {
    return {
      type: 'info',
      title: 'Completed' + completed + '. ' + (n ? 'Option ' + n + ' was final.' : 'An option was final.'),
      description: (gone.siteName || site) + ' was deleted' + on(fmt(c.fmtDate, gone.at)) + (role === 'client' || !gone.by ? '' : ' by ' + gone.by) +
        '. This RTA and its history are kept.' + deletedRts(rta, c),
      links: [],
      deleted: true
    }
  }

  var links = []

  if (!c.embedded) {
    links.push('site')
  }

  // No RTS (a Tower Co client, or a legacy RTA completed without one): no link. "Completed without an RTS" (XOL-693) has no link either.
  var hasRts = !c.tcClient && rtsOf(rta) !== null
  var outcome = c.tcClient ? null : rtsOutcome(rta)
  var rtsLine = ''

  if (outcome === 'linked') {
    rtsLine = ' ' + telco + '\'s existing RTS' + (hasRts ? ' #' + rtsOf(rta) : '') + ' was linked.'
  } else if (outcome === 'none') {
    rtsLine = ' No RTS was opened or linked.'
  } else if (hasRts) {
    rtsLine = ' An RTS was opened for ' + telco + '.'
  }

  if (hasRts) {
    links.push('rts')
  }

  return {
    type: 'success',
    title: 'Completed' + completed + ': ' + site + ' was created from ' + (n ? 'option ' + n : 'an option') + '.' + siteNow(rta, c, site) +
      rtsLine,
    description: '',
    links: links,
    deleted: false
  }
}

// The option a reset names, by ITS ID (P-S20: `lastReset.optionId`), shown with its current number; the number the reset was written
// with (`optionNo`) is the fallback for a reset without an id, or whose option is not among the rows the caller has.
function resetOptionNo (reset, options) {
  var list = Array.isArray(options) ? options : []

  if (reset.optionId !== undefined && reset.optionId !== null && reset.optionId !== '') {
    for (var i = 0; i < list.length; i++) {
      if (list[i] && Number(list[i].id) === Number(reset.optionId) && list[i].optionNo) {
        return list[i].optionNo
      }
    }
  }

  return reset.optionNo || null
}

// Is the name the API stamped (`lastReset.by`: trimmed, at most 88 characters) the viewer's? Neither known: no.
function sameName (stamped, viewer) {
  var a = stamped === undefined || stamped === null ? '' : String(stamped).trim()
  var b = viewer === undefined || viewer === null ? '' : String(viewer).trim().slice(0, 88)

  return a !== '' && a === b
}

/**
 * The Tower Co's notice that a review change, or its own Decline, cleared its saved decision (UX spec 4.2):
 * { title, description } while `state.rta.lastReset` is set, for a Tower Co viewer; otherwise null. The client never sees it.
 *
 *   submit   a review of the approved option was submitted: "{Telco} changed its review of option 2 on {date} {time}. Your remarks
 *            are kept; decide again when an option is approved." (a Tower Co user: "{name} ({Tower Co}) changed the review of ...")
 *   decline  "You declined option 2 on {date} {time}. Your remarks are kept." (another manager's: "{name} ({Tower Co}) declined ..."; no name stamped: "{Tower Co} declined ...")
 *
 * The v1 causes `edit` (the live pick) and `withdraw` are gone: the undo happens at Submit (47 as changed) and there is no Withdraw.
 */
export function resetNotice (rta, ctx) {
  var c = ctx || {}
  var reset = rtaState(rta).lastReset

  if (!reset || typeof reset !== 'object' || c.role === 'client') {
    return null
  }

  var tc = c.towerCo || 'the Tower Co'
  var telco = c.telco || 'the client'
  var when = fmt(c.fmtDateTime, reset.at)
  var no = resetOptionNo(reset, c.options)
  var n = no ? 'option ' + no : 'an option'
  var title = 'Your saved decision was cleared'
  var tail = on(when) + '. Your remarks are kept'

  switch (reset.cause) {
    case 'submit':
      // B8 (decisions 76, 77; API DEV-36): the Tower Co may review for the client, and `byParty` says who made the change.
      if (reset.byParty === 'towerco') {
        return {
          title: title,
          description: (reset.by ? reset.by + ' (' + tc + ')' : tc) + ' changed the review of ' + n + tail + '; decide again when an option is approved.'
        }
      }

      return {
        title: title,
        description: telco + ' changed its review of ' + n + tail + '; decide again when an option is approved.'
      }
    case 'decline':
      // "You declined" only when the decliner is the viewer: `lastReset.by` is the decliner's name as the API stamps it (cut to 88 characters) and
      // `ctx.userName` the viewer's. Anyone else is named ("{name} ({Tower Co}) declined"); with no name stamped, the Tower Co is ("{Tower Co} declined").
      if (sameName(reset.by, c.userName)) {
        return { title: title, description: 'You declined ' + n + tail + '.' }
      }

      return { title: title, description: (reset.by ? reset.by + ' (' + tc + ')' : tc) + ' declined ' + n + tail + '.' }
  }

  return null
}

/**
 * The header's cue line: { kind, ... }
 *   terminal   { text }              "Completed on {date}" (+ " · site deleted"), "Rejected on {date}"
 *   turn       { text }              "Your turn" tag, then the one task (rtaStage.yourTurnTask)
 *   waiting    { name, ending }      "Waiting on: <b>{name}</b>{ending}"
 */
export function cue (rta, ctx) {
  var c = ctx || {}
  var tc = c.towerCo || 'the Tower Co'
  var telco = c.telco || 'the client'

  if (rta && rta.status === 'Closed') {
    var date = fmt(c.fmtDate, completedAt(rta))

    return { kind: 'terminal', text: 'Completed' + on(date) + (siteDeleted(rta) ? ' · site deleted' : '') }
  }

  if (rta && rta.status === 'Rejected') {
    return { kind: 'terminal', text: 'Rejected' + on(fmt(c.fmtDate, rta.completed_at)) }
  }

  var task = yourTurnTask(rta, c.org, { telco: telco, towerCo: tc })

  if (task) {
    return { kind: 'turn', text: task }
  }

  var wait = waitingOn(rta, c.org)

  return { kind: 'waiting', name: wait.party === 'client' ? telco : tc, ending: wait.ending || '' }
}

/** After the reference in the header: "Stage 2 of 5", "Completed" or "Rejected" (UX spec 4.3). */
export function stageSuffix (rta) {
  var state = stepState(rta)

  if (state.terminal === 'completed') {
    return 'Completed'
  }

  if (state.terminal === 'rejected') {
    return 'Rejected'
  }

  return 'Stage ' + state.current + ' of 5'
}
